// src/components/ConversationDetail.js
import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import axios from '../axiosConfig';

const ConversationDetail = ({ initialConversation, fetchConversations }) => {
  const [conversation, setConversation] = useState(initialConversation);
  const [newMessage, setNewMessage] = useState('');

  // Function to fetch the latest messages for the current conversation
  const fetchLatestMessages = useCallback(async () => {
    try {
      const response = await axios.get(`/conversations/${conversation.thread_id}/messages`);
      setConversation(response.data); // Update the conversation state with new data
    } catch (err) {
      console.error('Failed to fetch latest messages', err);
    }
  }, [conversation.thread_id]);

  // Set up the interval to fetch latest messages every 10 seconds
  useEffect(() => {
    if (initialConversation.active) {
      const interval = setInterval(() => {
        fetchLatestMessages();
      }, 10000); // 30 seconds

      // Clean up the interval on component unmount
      return () => clearInterval(interval);
    }
  }, [fetchLatestMessages, initialConversation.active]);

  const handleSendMessage = async () => {
    if (newMessage.trim() === '') return;

    try {
      await axios.post(`/conversations/${conversation.thread_id}/messages`, {
        content: newMessage,
        type: 'store',
      });

      setNewMessage('');
      fetchConversations(); // Refresh the conversation list to include the new message
      fetchLatestMessages(); 
    } catch (err) {
      console.error('Failed to send message', err);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    // const day = String(date.getDate()).padStart(2, '0');
    // const month = String(date.getMonth() + 1).padStart(2, '0');
    // const year = String(date.getFullYear());

    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');

    return `${hour}:${minute}`;
  }

  // Update the conversation state whenever the prop changes
  useEffect(() => {
    setConversation(initialConversation);
  }, [initialConversation]);

  return (
    <Container>
      <Header>{conversation.username}</Header>
      <MessageContainer>
        {conversation.messages
          .filter((message) => {
            if (message.content.trim() === "" || message.role === 'tool') {
              return false;
            }
            return true;
          })
          .map((message, index) => (
            <Message key={index} isAI={message.role === "ai" || message.role === "store"}>
              <span dangerouslySetInnerHTML={{ __html: message.content.split('\n').join('<br />') }} /><br></br>
              <TimeSent>{formatDate(message.created_at)}</TimeSent>
            </Message>
          ))}
      </MessageContainer>
      <InputContainer>
        <MessageInput
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type a message..."
        />
        <SendButton onClick={handleSendMessage}>Send</SendButton>
      </InputContainer>
    </Container>
  );
};

const Container = styled.div`
  padding: 20px;
  overflow-y: auto;
`;

const Header = styled.div`
  font-weight: bold;
  margin-bottom: 20px;
`;

const MessageContainer = styled.div`
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
`;

const Message = styled.div`
  background: ${(props) => (props.isAI ? "#d9fdd3" : "#fff")};
  text-align: left;
  max-width: 60%;
  padding: 10px;
  border-radius: 5px;
  margin-bottom: 5px;
  border: ${(props) => (props.isAI ? '1px solid #ccc' : 'none')};
  align-self: ${(props) => (props.isAI ? 'flex-end' : 'flex-start')};
  box-shadow: ${(props) =>
    props.isAI ? '0px 1px 3px rgba(0, 0, 0, 0.2)' : '0px 1px 3px rgba(0, 0, 0, 0.3)'};
`;

const TimeSent = styled.span`
    font-size: 11px;
`;

const InputContainer = styled.div`
  display: flex;
  padding: 10px;
  border-top: 1px solid #ddd;
`;

const MessageInput = styled.input`
  flex: 1;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  margin-right: 10px;
`;

const SendButton = styled.button`
  padding: 10px 20px;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;

  &:hover {
    background-color: #45a049;
  }
`;

export default ConversationDetail;
